import React, { FunctionComponent, useEffect, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { pathOr } from 'ramda';
import { match } from 'react-router-dom';
import { Grid } from 'semantic-ui-react';
import classnames from 'classnames';
import withStyles from 'react-jss';

import { useMerchantOverview } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/MerchantOverview.context';
import { MerchantQuickLookPlaceholder } from 'components/Placeholder';
import { useDatePicker } from 'components/DatePicker/DatePicker.context';
import { ClassProp } from 'helpers/types';
import { opacityAnimation } from 'config';
import Query from 'components/Query';
import { GET_IS_OPEN } from 'components/Header/components/Filters/Filters.queries';

import {
  GET_KEY_METRICS,
  GET_VENUE_TYPE,
  GET_SHOW_DELTAS,
  GET_CASHBACK_SEGMENT,
} from './QuickLook.queries';
import KeyMetricsSection from './components/KeyMetrics';

export type KeyMetric = {
  amount: number;
  amountCleared: number;
  previousAmount: number;
  nonClearedPending: number;
};

export type KeyMetrics = {
  keyMetrics: {
    transactionCount: KeyMetric;
    aov: KeyMetric;
    frequency: KeyMetric;
    revenue: KeyMetric;
    lift: KeyMetric;
    roas: KeyMetric;
    attribution: KeyMetric;
    [key: string]: KeyMetric;
  };
};

type Props = {
  classes: ClassProp<typeof styles>;
  match: match;
  locked?: boolean;
};

export type QueryResponse = {
  loading: boolean;
  data: KeyMetrics;
};

const styles = {
  section: {
    alignItems: 'flex-end !important',
    padding: '0 !important',
  },
  KeyMetrics: {
    padding: '0 !important',

    '&&&&.locked': {
      animation: `${opacityAnimation} .5s linear`,
      position: 'fixed !important',
      zIndex: 100,
      top: '9.95rem',
      width: '72.5rem !important',

      '&.filtersIsOpen': {
        top: '13.95rem',
      },

      '&.loading': {
        top: '9.45rem',
      },
    },
  },
};

const renderQuickLook = (
  { classes, locked }: Props,
  calcPrevious: boolean,
  filtersIsOpen: boolean,
) => (keyMetricsPayload: QueryResponse) => {
  if (keyMetricsPayload.loading) {
    return (
      <div className={classnames(classes.KeyMetrics, { locked }, 'loading')}>
        <MerchantQuickLookPlaceholder />
      </div>
    );
  }

  return (
    <Grid>
      <Grid.Row className={classes.section}>
        <Grid.Column
          className={classnames(classes.KeyMetrics, { locked, filtersIsOpen })}
        >
          <KeyMetricsSection
            keyMetrics={keyMetricsPayload.data}
            locked={locked}
            showDeltas={calcPrevious}
          />
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

const QuickLook: FunctionComponent<Props> = ({ match, ...props }) => {
  const { userProfile } = useMerchantOverview();
  const { startDate, endDate } = useDatePicker();
  const venueTypePayload = useQuery(GET_VENUE_TYPE);
  const showDeltasPayload = useQuery(GET_SHOW_DELTAS);
  const isOpenPayload = useQuery(GET_IS_OPEN);
  const { loading, data } = useQuery(GET_CASHBACK_SEGMENT);
  const [cashbackPercentage, setCashbackPercentage] = useState<string>('');
  const [cashbackDollarAmt, setCashbackDollarAmt] = useState<string>('');
  let { merchantId, merchantName } = userProfile;

  merchantId = pathOr(merchantId, ['params', 'merchantId'], match);
  merchantName = pathOr(merchantName, ['params', 'merchantName'], match);

  const venueType = pathOr(null, ['data', 'venueType'], venueTypePayload);
  const calcPrevious = pathOr(false, ['data', 'showDeltas'], showDeltasPayload);
  const filtersIsOpen = pathOr(false, ['data', 'filtersIsOpen'], isOpenPayload);
  useEffect(() => {
    if (loading && !data) return;
    const cashbackSegment = pathOr(
      'null|null',
      ['cashbackSegment'],
      data,
    ).split('|');
    const type = cashbackSegment[0];
    const value = cashbackSegment[1];
    setCashbackPercentage(type === 'percent' ? value : null);
    setCashbackDollarAmt(type === 'dollar' ? value : null);
  }, [loading, data]);

  return (
    <Query
      enableProgressInfo
      query={GET_KEY_METRICS}
      render={renderQuickLook(props as Props, calcPrevious, filtersIsOpen)}
      variables={{
        params: {
          merchantId,
          merchantName,
          startDate,
          endDate,
          venueType,
          calcPrevious,
          cashbackPercentage: parseInt(cashbackPercentage, 10),
          cashbackDollarAmt: parseInt(cashbackDollarAmt, 10),
        },
      }}
    />
  );
};

export default withStyles(styles)(QuickLook);
